import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { practitionerLinkApi } from "../api/practitionerLink";

// "Does this client have a live practitioner relationship right now?"
//
// One definition, two callers. ProtocolCard.jsx has always answered this
// on /dashboard by crossing GET /practitioners/links with GET
// /practitioner-protocols/assigned; the Journal tab now needs the same
// answer to decide whether a share affordance exists at all. The
// resolution rule lives here so the two surfaces cannot drift into
// disagreeing about who the practitioner is.
//
// The rule, unchanged from ProtocolCard's original inline version:
//   · consent is live, not historical — only links with consent_given
//     count, because revoking consent pauses the relationship
//   · the protocol must be `active`
//   · the link returned is the one belonging to that protocol's
//     practitioner
//
// No link → callers render nothing. A share button that opens onto an
// empty relationship is worse than no button.

export function resolveActivePractitioner(links, protocols) {
  const linkList = Array.isArray(links) ? links : [];
  const protocolList = Array.isArray(protocols) ? protocols : [];
  const consented = linkList
    .filter((l) => l.consent_given)
    .map((l) => l.practitioner_id);
  const protocol =
    protocolList.find(
      (p) => p.status === "active" && consented.includes(p.practitioner_id)
    ) || null;
  const link = protocol
    ? linkList.find((l) => l.practitioner_id === protocol.practitioner_id) || null
    : null;
  return { protocol, link };
}

// The link for the active, consented protocol — or null. Soft-fails to
// null on any transport error: an unreachable endpoint means the
// affordance is simply absent, never a broken screen.
export function useActivePractitionerLink() {
  const { token } = useAuth();
  const [link, setLink] = useState(null);

  useEffect(() => {
    if (!token) return;
    let dead = false;
    (async () => {
      try {
        const [linksRes, protoRes] = await Promise.all([
          practitionerLinkApi.myLinks(token),
          practitionerLinkApi.assigned(token),
        ]);
        if (dead) return;
        const { link: active } = resolveActivePractitioner(
          linksRes?.links || [],
          protoRes?.protocols || []
        );
        setLink(active);
      } catch {
        if (!dead) setLink(null);
      }
    })();
    return () => { dead = true; };
  }, [token]);

  // Derived, not stored, for the signed-out case: a stale link must not
  // outlive the session that fetched it, and clearing it from the
  // effect body would be a cascading render for a value we can just
  // read through.
  return token ? link : null;
}
